import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import {
  type UploadData,
  initialUploadData,
} from './upload.store.internals';
import { type UploadActions, createActions } from './upload.store.actions';

export type {
  UploadItem,
  UploadBatch,
  UploadStats,
} from './upload.store.internals';

export type UploadStore = UploadData & UploadActions;

// Public name retained for backwards-compatibility with the original export
// name and any external `import type { UploadState }` consumers.
export type UploadState = UploadStore;

/* ============================================================================
 * UPLOAD STORE
 * ============================================================================
 *
 * Two real files (B1 pattern):
 *
 *   • `upload.store.internals.ts` — `UploadData` shape + `initialUploadData`
 *     + ID generators (`generateItemId` / `generateBatchId`).
 *
 *   • `upload.store.actions.ts` — `UploadActions` interface +
 *     `createActions` factory: createBatch / uploadBatch / uploadSingle /
 *     getTotalProgress (public) plus addToQueue / updateProgress
 *     (internal — called via `get()` from other actions).
 *
 * Surface trimmed during the split: 17 unused exported selectors and ~15
 * unused action methods (pause/resume/cancel/retry/process/getXItems/
 * canUpload/openUploadModal/closeUploadModal/setDragActive/...) were
 * dropped — no consumer ever called them. The write-only `settings` slice,
 * `updateSettings`, `updateBatchProgress` and the never-called `reset`
 * followed in 2026-06. Re-add only with a real call site.
 * ==========================================================================*/
export const useUploadStore = create<UploadStore>()(
  subscribeWithSelector(
    immer((...a) => ({
      ...initialUploadData,
      ...createActions(...a),
    }))
  )
);
